import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Tv, Search, RefreshCw } from "lucide-react";

const EMPTY_IMG =
  "https://images.unsplash.com/photo-1785682231847-93265d8e633d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByYWNrJTIwZ2xvd2luZyUyMGRhcmt8ZW58MHx8fHwxNzg2MzQ3ODI5fDA&ixlib=rb-4.1.0&q=85";

export default function ChannelsTable({ refreshKey }) {
  const [channels, setChannels] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("live");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (term, k) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/channels", { params: { search: term, kind: k, limit: 50 } });
      setChannels(data.channels || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(search, kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, kind]);

  useEffect(() => {
    const t = setTimeout(() => load(search, kind), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="flex h-full flex-col rounded-sm border border-zinc-800 bg-zinc-900 p-6" data-testid="channels-table">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Tv className="h-5 w-5 text-emerald-400" />
          <h2 className="font-heading text-lg font-semibold tracking-tight text-zinc-100">Content</h2>
          <span className="rounded-sm border border-zinc-800 bg-zinc-950 px-2 py-0.5 font-mono text-xs text-zinc-400">
            {total}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 rounded-sm border border-zinc-800 bg-zinc-950 p-0.5">
            {[["live", "Channels"], ["movie", "Movies"], ["series", "Series"]].map(([k, label]) => (
              <button
                key={k}
                data-testid={`kind-${k}`}
                onClick={() => setKind(k)}
                className={`rounded-sm px-2.5 py-1 font-mono text-xs uppercase tracking-wider transition-colors duration-200 ${
                  kind === k ? "bg-emerald-500/15 text-emerald-400" : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="channel-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-36 rounded-sm border border-zinc-800 bg-zinc-950 py-1.5 pl-8 pr-2 font-mono text-xs text-zinc-100 outline-none transition-colors duration-200 focus:border-emerald-500 focus:w-48"
            />
          </div>
        </div>
      </div>

      {channels.length === 0 ? (
        <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden rounded-sm border border-dashed border-zinc-800 py-14 text-center">
          <img src={EMPTY_IMG} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.06]" />
          <RefreshCw className={`relative mb-3 h-6 w-6 text-zinc-600 ${loading ? "animate-spin" : ""}`} />
          <p className="relative font-mono text-sm text-zinc-400">
            {loading ? "Loading channels…" : "No channels cached yet"}
          </p>
          <p className="relative mt-1 px-6 text-xs text-zinc-600">
            Save your Xtream panel URL and run "Test Connection" to pull the channel list.
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto rounded-sm border border-zinc-800">
          <table className="w-full text-left">
            <thead className="sticky top-0 z-10 bg-zinc-950">
              <tr className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-2 font-normal">#</th>
                <th className="px-3 py-2 font-normal">Logo</th>
                <th className="px-3 py-2 font-normal">Name</th>
                <th className="px-3 py-2 font-normal">Category</th>
                <th className="px-3 py-2 font-normal">ID</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => (
                <tr
                  key={c.stream_id}
                  data-testid={`channel-row-${c.stream_id}`}
                  className="border-b border-zinc-800 transition-colors duration-200 hover:bg-zinc-800/50"
                >
                  <td className="px-3 py-2 font-mono text-xs text-zinc-500">{c.num ?? "—"}</td>
                  <td className="px-3 py-2">
                    {c.icon ? (
                      <img src={c.icon} alt="" className="h-6 w-6 rounded-sm object-contain" onError={(e) => (e.target.style.opacity = 0)} />
                    ) : (
                      <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-zinc-800">
                        <Tv className="h-3 w-3 text-zinc-600" />
                      </div>
                    )}
                  </td>
                  <td className="max-w-[220px] truncate px-3 py-2 text-sm text-zinc-200">{c.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-500">{c.category_name || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-600">{c.stream_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
